import React from "react";
import styled, { keyframes } from "styled-components";
// Icons
import { FaStar } from "react-icons/fa6";
// Components
import LazyImage from "../Elements/LazyImage";
// Assets
import ContactImg1 from "../../assets/optimized/compliance-verification.webp";
import ContactImg2 from "../../assets/optimized/risk-management-analytics.webp";
import ContactImg3 from "../../assets/optimized/business-analytics-dashboard.webp";

// Animation keyframes for Contact bubbles
const contactFloat = keyframes`
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
    opacity: 0.5;
  }
  25% {
    transform: translateY(-12px) rotate(1deg);
    opacity: 0.8;
  }
  75% {
    transform: translateY(8px) rotate(-1deg);
    opacity: 0.6;
  }
`;

// Animated background component for Contact
const ContactAnimatedBackground = () => (
  <ContactBackgroundContainer>
    <ContactFloatingOrb delay="0s" size="65px" />
    <ContactFloatingOrb delay="1.5s" size="80px" />
    <ContactFloatingOrb delay="3s" size="50px" />
    <ContactFloatingOrb delay="4.5s" size="95px" />
    <ContactFloatingOrb delay="6s" size="70px" />
    <ContactFloatingOrb delay="7.5s" size="60px" />
  </ContactBackgroundContainer>
);

export default function Contact() {
  return (
    <Wrapper id="contact">
      <div className="lightBg" style={{ position: "relative", overflow: "hidden" }}>
        <ContactAnimatedBackground />
        <div className="container" style={{ position: "relative", zIndex: 2 }}>
          <HeaderInfo>
            <h1 className="font40 extraBold">Get In Touch</h1>
            <p className="font16">
              Ready to transform your business with enterprise-grade intelligence? Contact us to learn how Anton Tech's five core pillars can drive growth across agriculture, compliance, insurance, sustainability, and analytics.
            </p>
          </HeaderInfo>
          
          <ContactContent>
            <ContactInfo>
              <ContactSection>
                <SectionTitle className="font24 extraBold">Contact Information</SectionTitle>
                <ContactItem>
                  <ContactLabel>Office:</ContactLabel>
                  <ContactValue>
                    <a href="https://www.google.com/maps?q=Plot+69184,+Block+8,+Botswana+Digital+%26+Innovation+Hub" target="_blank" rel="noopener noreferrer">
                      Plot 69184, Block 8, Botswana Digital & Innovation Hub
                    </a>
                  </ContactValue>
                </ContactItem>
                
                <ContactItem>
                  <ContactLabel>Email:</ContactLabel>
                  <ContactValue>
                    <a href="mailto:info@anton.co.bw">info@anton.co.bw</a>
                  </ContactValue>
                </ContactItem>
              </ContactSection>
              
              <AccessSection>
                {/* <SectionTitle className="font24 extraBold">Get Started</SectionTitle> */}
                <GetStartedButton href="https://app.anton.co.bw/login" target="_blank" rel="noopener noreferrer">
                  <FaStar style={{ marginRight: '8px', fontSize: '14px' }} /> Get Started Today
                </GetStartedButton>
              </AccessSection>
            </ContactInfo>
            
            <ImageGallery>
              <ImagesWrapper>
                <ImageWrapper>
                  <LazyImage src={ContactImg1} alt="Compliance & Verification Solutions" />
                </ImageWrapper>
                <ImageWrapper>
                  <LazyImage src={ContactImg2} alt="Risk Management & Analytics" />
                </ImageWrapper>
                <ImageWrapper>
                  <LazyImage src={ContactImg3} alt="Enterprise Business Intelligence" />
                </ImageWrapper>
              </ImagesWrapper>
            </ImageGallery>
          </ContactContent>
        </div>
      </div>
    </Wrapper>
  );
}

const Wrapper = styled.section`
  width: 100%;
  padding: 100px 0;
`;

const HeaderInfo = styled.div`
  text-align: center;
  margin-bottom: 80px;
  
  h1 {
    margin-bottom: 20px;
    background: linear-gradient(135deg, #079487 0%, #079487 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  p {
    color: #666;
    max-width: 700px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const ContactContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const ContactSection = styled.div`
  background: white;
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
`;

const AccessSection = styled.div`
  background: linear-gradient(135deg, rgba(7, 148, 135, 0.05) 0%, rgba(7, 148, 135, 0.05) 100%);
  padding: 40px;
  border-radius: 12px;
  border: 2px solid rgba(7, 148, 135, 0.1);
  text-align: center;
`;

const SectionTitle = styled.h3`
  color: #079487;
  margin-bottom: 25px;
`;

const ContactItem = styled.div`
  margin-bottom: 20px;
`;

const ContactLabel = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`;

const ContactValue = styled.div`
  color: #666;
  
  a {
    color: #079487;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const GetStartedButton = styled.a`
  display: block;
  background: transparent;
  color: #079487;
  padding: 15px 30px;
  border: 2px solid #079487;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background: #079487;
    color: white;
    transform: translateY(-2px);
  }
`;

const ImageGallery = styled.div`
  text-align: center;
`;

const ImagesWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
`;

const ImageWrapper = styled.div`
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
  
  img {
    width: 100%;
    height: 200px;
    object-fit: cover;
  }
`;

// Contact section animated background components
const ContactBackgroundContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  z-index: 1;
`;

const ContactFloatingOrb = styled.div`
  position: absolute;
  width: ${props => props.size || '65px'};
  height: ${props => props.size || '65px'};
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(7, 148, 135, 0.06) 0%, rgba(102, 187, 106, 0.04) 100%);
  animation: ${contactFloat} 14s ease-in-out infinite;
  animation-delay: ${props => props.delay};
  
  &:nth-child(1) {
    top: 25%;
    right: 12%;
  }
  
  &:nth-child(2) {
    bottom: 35%;
    left: 8%;
  }
  
  &:nth-child(3) {
    top: 65%;
    right: 30%;
  }
  
  &:nth-child(4) {
    top: 45%;
    left: 25%;
  }
  
  &:nth-child(5) {
    bottom: 20%;
    right: 20%;
  }
  
  &:nth-child(6) {
    top: 10%;
    left: 35%;
  }
`;
